/** @file Greedy label placement over a uniform grid of placed boxes (near-linear), capped per layer. */
import { OVERLAY_STYLE } from '../overlayStyle';
import { anchorInView } from './anchorInView';

/** What placement reads of a label: its text (only the length) and its anchor in pixels. */
export interface LabelAnchor {
  text: string;
  x: number;
  y: number;
}

/** Result of one layoutLabels call as parallel arrays; valid until the next call (module scratch, reused). */
export interface LabelLayout {
  /** Placed labels, in request order. */
  count: number;
  /** Visible labels with text left out because their layer already placed `OVERLAY_STYLE.maxLabelsPerLayer`. */
  dropped: number;
  /** Request index of each placed label. */
  index: Int32Array;
  /** [x, y, w, h] of each placed label, 4 numbers per label. */
  box: Float64Array;
  /** 1 when the label was pushed out to an outer ring and needs a leader line. */
  leader: Uint8Array;
  /** Box overlap tests plus grid entries written by the call: a machine-independent cost the tests bound. */
  work: number;
}

/** Rings of candidate positions tried before falling back to an overlapping spot. */
const RINGS = 4;
/** Candidate sides per ring as x / y selectors (-1 left/above, 0 centred, 1 right/below), tried in this order. */
const DX = Int8Array.of(1, 1, -1, -1, 0, 0, 1, -1);
const DY = Int8Array.of(-1, 1, -1, 1, -1, 1, 0, 0);
/** A huge view gets bigger cells, never more than this many per side. */
const MAX_CELLS_PER_SIDE = 256;

const layout: LabelLayout = { count: 0, dropped: 0, index: new Int32Array(64), box: new Float64Array(256), leader: new Uint8Array(64), work: 0 };
/**
 * Uniform grid over the view. Each cell lists the placed boxes that touch it oldest first (`head` -> `next` ...,
 * appended at `tail`), the order the pre-grid placement scanned in: the ring boxes that block a crowded anchor are
 * met before the pile of fallback boxes stacked on it later.
 */
const grid = {
  size: 1, cols: 1, rows: 1, entries: 0,
  head: new Int32Array(64), tail: new Int32Array(64), next: new Int32Array(256), owner: new Int32Array(256),
};

/**
 * Clears the layout and sizes the grid: cells about one label high (|height|: a negative glyph height still makes
 * boxes that tall, only upwards), at least 1 px, at most MAX_CELLS_PER_SIDE per side.
 */
function reset(width: number, height: number, labelH: number): void {
  const finite = Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0;
  const base = Math.abs(labelH) > 1 ? Math.abs(labelH) : 1;
  grid.size = finite ? Math.max(base, width / MAX_CELLS_PER_SIDE, height / MAX_CELLS_PER_SIDE) : base;
  grid.cols = finite ? Math.max(1, Math.min(MAX_CELLS_PER_SIDE, Math.ceil(width / grid.size))) : 1;
  grid.rows = finite ? Math.max(1, Math.min(MAX_CELLS_PER_SIDE, Math.ceil(height / grid.size))) : 1;
  const cells = grid.cols * grid.rows;
  if (grid.head.length < cells) {
    grid.head = new Int32Array(cells);
    grid.tail = new Int32Array(cells);
  }
  grid.head.fill(-1, 0, cells);
  grid.entries = 0;
  layout.count = 0;
  layout.dropped = 0;
  layout.work = 0;
}

/** Grid index along one axis of coordinate v, clamped (NaN stays NaN, so a loop over it runs zero times). */
const cellOf = (v: number, cells: number): number => Math.min(cells - 1, Math.max(0, Math.floor(v / grid.size)));

/** True when placed label k's box shares area with [x, y, w, h] (the strict test placement always used). */
function overlapsPlaced(k: number, x: number, y: number, w: number, h: number): boolean {
  const b = layout.box, o = 4 * k;
  const bx = b[o] ?? NaN, by = b[o + 1] ?? NaN, bw = b[o + 2] ?? NaN, bh = b[o + 3] ?? NaN;
  return bx < x + w && x < bx + bw && by < y + h && y < by + bh;
}

/** True when a placed box listed in `cell` shares area with [x, y, w, h] (scanned oldest first). */
function hitsCell(cell: number, x: number, y: number, w: number, h: number): boolean {
  for (let e = grid.head[cell] ?? -1; e >= 0; e = grid.next[e] ?? -1) {
    layout.work += 1;
    if (overlapsPlaced(grid.owner[e] ?? 0, x, y, w, h)) return true;
  }
  return false;
}

/** True when [x, y, w, h] overlaps a placed box; only the cells it covers are searched. */
function blocked(x: number, y: number, w: number, h: number): boolean {
  const c0 = cellOf(Math.min(x, x + w), grid.cols), c1 = cellOf(Math.max(x, x + w), grid.cols);
  const r1 = cellOf(Math.max(y, y + h), grid.rows);
  for (let r = cellOf(Math.min(y, y + h), grid.rows); r <= r1; r++) {
    for (let c = c0; c <= c1; c++) if (hitsCell(r * grid.cols + c, x, y, w, h)) return true;
  }
  return false;
}

/** Makes room for `n` grid entries (doubling, contents kept). */
function reserveEntries(n: number): void {
  if (grid.next.length >= n) return;
  const size = Math.max(n, 2 * grid.next.length), next = new Int32Array(size), owner = new Int32Array(size);
  next.set(grid.next);
  owner.set(grid.owner);
  grid.next = next;
  grid.owner = owner;
}

/** Makes room for `n` placed labels (doubling, contents kept). */
function reserveLabels(n: number): void {
  if (layout.index.length >= n) return;
  const size = Math.max(n, 2 * layout.index.length), index = new Int32Array(size), box = new Float64Array(4 * size), leader = new Uint8Array(size);
  index.set(layout.index);
  box.set(layout.box);
  leader.set(layout.leader);
  layout.index = index;
  layout.box = box;
  layout.leader = leader;
}

/** Appends placed label `k` at the tail of `cell`'s list. */
function append(cell: number, k: number): void {
  const e = grid.entries++;
  grid.owner[e] = k;
  grid.next[e] = -1;
  if ((grid.head[cell] ?? -1) < 0) grid.head[cell] = e;
  else grid.next[grid.tail[cell] ?? 0] = e;
  grid.tail[cell] = e;
  layout.work += 1;
}

/** Lists placed label `k` in every cell its box covers (a NaN box covers none: it can never overlap). */
function insert(k: number, x: number, y: number, w: number, h: number): void {
  const c0 = cellOf(Math.min(x, x + w), grid.cols), c1 = cellOf(Math.max(x, x + w), grid.cols);
  const r0 = cellOf(Math.min(y, y + h), grid.rows), r1 = cellOf(Math.max(y, y + h), grid.rows);
  if (!(c0 <= c1 && r0 <= r1)) return;
  reserveEntries(grid.entries + (c1 - c0 + 1) * (r1 - r0 + 1));
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) append(r * grid.cols + c, k);
  }
}

/** Appends a placed label to the layout and the grid. */
function push(index: number, x: number, y: number, w: number, h: number, leader: boolean): void {
  const k = layout.count++, o = 4 * k;
  reserveLabels(k + 1);
  layout.index[k] = index;
  layout.box[o] = x;
  layout.box[o + 1] = y;
  layout.box[o + 2] = w;
  layout.box[o + 3] = h;
  layout.leader[k] = leader ? 1 : 0;
  insert(k, x, y, w, h);
}

/** Top-left coordinate of a box of `size` on side s (-1, 0, 1) of anchor coordinate `a` at distance `gap`. */
const side = (a: number, s: number, gap: number, size: number): number => (s > 0 ? a + gap : s < 0 ? a - gap - size : a - size / 2);

/** First free candidate (ring by ring, side by side), else the ring-0 north-east box clamped into the view. */
function placeOne(index: number, label: LabelAnchor, w: number, h: number, width: number, height: number): void {
  for (let ring = 0; ring < RINGS; ring++) {
    const gap = OVERLAY_STYLE.labelGap + ring * (h + OVERLAY_STYLE.labelGap);
    for (let d = 0; d < DX.length; d++) {
      const x = Math.round(side(label.x, DX[d] ?? 0, gap, w)), y = Math.round(side(label.y, DY[d] ?? 0, gap, h));
      const inside = x >= 0 && y >= 0 && x + w <= width && y + h <= height;
      if (inside && !blocked(x, y, w, h)) {
        push(index, x, y, w, h, ring > 0);
        return;
      }
    }
  }
  const x = Math.round(side(label.x, 1, OVERLAY_STYLE.labelGap, w)), y = Math.round(side(label.y, -1, OVERLAY_STYLE.labelGap, h));
  push(index, Math.max(0, Math.min(x, width - w)), Math.max(0, Math.min(y, height - h)), w, h, false);
}

/** Exclusive end of layer `g` in the request list (past the given ends, one layer runs to the end). */
const layerEnd = (ends: readonly number[] | undefined, g: number): number => (ends !== undefined && g < ends.length ? (ends[g] ?? Infinity) : Infinity);

/** True for a label placement considers at all: non-empty text, anchor inside the view (edges included). */
const placeable = (label: LabelAnchor | undefined, width: number, height: number): label is LabelAnchor =>
  label !== undefined && label.text.length > 0 && anchorInView(label.x, label.y, width, height);

/**
 * Places labels in request order (earlier = higher priority): empty texts and anchors outside the view are skipped;
 * box = text + `OVERLAY_STYLE.labelPad` on every side. `ends` splits the requests into layers (exclusive end
 * indices, one per layer; default: one layer). Each layer places at most `OVERLAY_STYLE.maxLabelsPerLayer` labels
 * and drops its remaining placeable ones (its lowest-priority requests), which then take no room.
 */
export function layoutLabels(labels: readonly LabelAnchor[], width: number, height: number, glyphW: number, glyphH: number, ends?: readonly number[]): LabelLayout {
  const pad = OVERLAY_STYLE.labelPad, h = glyphH + 2 * pad;
  reset(width, height, h);
  let layer = 0, end = layerEnd(ends, 0), placed = 0;
  for (let i = 0; i < labels.length; i++) {
    while (i >= end) {
      end = layerEnd(ends, ++layer);
      placed = 0;
    }
    const label = labels[i];
    if (!placeable(label, width, height)) continue;
    if (placed >= OVERLAY_STYLE.maxLabelsPerLayer) {
      layout.dropped += 1;
      continue;
    }
    placeOne(i, label, label.text.length * glyphW + 2 * pad, h, width, height);
    placed += 1;
  }
  return layout;
}
